// ============================================================
// Teacher Service (Mock API)
// ============================================================
// Simulates backend operations scoped to a schoolId.

import { mockTeachers } from '@/mock/teachers';
import { mockTeacherAssignments } from '@/mock/teacherAssignments';
import { mockTeacherDashboardData } from '@/mock/teacherDashboard';
import { mockTeacherNotifications } from '@/mock/teacherNotifications';
import { mockTeacherHomework } from '@/mock/teacherHomework';
import { mockTeacherClasswork } from '@/mock/teacherClasswork';
import { mockTeacherTimetable } from '@/mock/teacherTimetable';
import { MOCK_DELAY } from '@/constants/app';

let teachersDb = [...mockTeachers];
let assignmentsDb = [...mockTeacherAssignments];
let homeworkDb = [...mockTeacherHomework];
let classworkDb = [...mockTeacherClasswork];
let notificationsDb = [...mockTeacherNotifications];
// Timetable and dashboard data are complex to mutate in mock, read-only for now
let dashboardDb = { ...mockTeacherDashboardData };
let timetableDb = { ...mockTeacherTimetable };

const delay = (ms = MOCK_DELAY.MEDIUM) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const teacherService = {
  // ── Management Operations (Owner/Principal) ────────────────

  getTeachers: async (schoolId) => {
    await delay();
    if (!schoolId) throw new Error('School ID is required');
    const schoolTeachers = teachersDb.filter((t) => t.schoolId === schoolId);
    return { success: true, data: schoolTeachers };
  },

  getTeacherById: async (schoolId, teacherId) => {
    await delay();
    const teacher = teachersDb.find((t) => t.schoolId === schoolId && t.id === teacherId);
    if (!teacher) throw new Error('Teacher not found');
    return { success: true, data: teacher };
  },

  createTeacher: async (schoolId, teacherData) => {
    await delay(MOCK_DELAY.LONG);
    if (!schoolId) throw new Error('School ID is required');

    const newTeacher = {
      ...teacherData,
      id: `teacher-${Date.now()}`,
      schoolId,
      status: 'active',
      joiningDate: teacherData.joiningDate || new Date().toISOString().split('T')[0],
    };

    teachersDb.unshift(newTeacher);
    return { success: true, data: newTeacher };
  },

  updateTeacher: async (schoolId, teacherId, updateData) => {
    await delay();
    const index = teachersDb.findIndex((t) => t.schoolId === schoolId && t.id === teacherId);
    if (index === -1) throw new Error('Teacher not found');

    const updatedTeacher = { ...teachersDb[index], ...updateData };
    teachersDb[index] = updatedTeacher;
    return { success: true, data: updatedTeacher };
  },

  deleteTeacher: async (schoolId, teacherId) => {
    await delay();
    const index = teachersDb.findIndex((t) => t.schoolId === schoolId && t.id === teacherId);
    if (index === -1) throw new Error('Teacher not found');

    // Soft delete (deactivate)
    teachersDb[index].status = 'inactive';
    return { success: true, message: 'Teacher deactivated successfully' };
  },

  // ── Assignment Operations ──────────────────────────────────

  getTeacherAssignments: async (schoolId, teacherId) => {
    await delay();
    const assignments = assignmentsDb.filter(
      (a) => a.schoolId === schoolId && a.teacherId === teacherId
    );
    return { success: true, data: assignments };
  },

  assignClassToTeacher: async (schoolId, teacherId, assignmentData) => {
    await delay();
    const newAssignment = {
      ...assignmentData,
      id: `assign-${Date.now()}`,
      teacherId,
      schoolId,
    };
    assignmentsDb.push(newAssignment);
    return { success: true, data: newAssignment };
  },

  removeAssignment: async (schoolId, assignmentId) => {
    await delay();
    assignmentsDb = assignmentsDb.filter(
      (a) => !(a.schoolId === schoolId && a.id === assignmentId)
    );
    return { success: true };
  },

  // ── Workspace Operations (Teacher Role) ────────────────────

  getTeacherDashboard: async (teacherId) => {
    await delay();
    const data = dashboardDb[teacherId] || {
      todaysClasses: [],
      stats: { totalClasses: 0, totalSubjects: 0, totalStudents: 0, classTeacherOf: '-' },
      attendanceSummary: { present: 0, absent: 0, late: 0, leave: 0, total: 0 },
      homeworkSummary: { pending: 0, submitted: 0, reviewed: 0 },
    };
    return { success: true, data };
  },

  getTeacherTimetable: async (teacherId) => {
    await delay();
    const timetable = timetableDb[teacherId] || [];
    return { success: true, data: timetable };
  },

  getTeacherNotifications: async (teacherId) => {
    await delay();
    const notifications = notificationsDb.filter(
      (n) => n.teacherId === teacherId || !n.teacherId
    );
    return { success: true, data: notifications };
  },

  markNotificationRead: async (notificationId) => {
    await delay(MOCK_DELAY.SHORT);
    const index = notificationsDb.findIndex((n) => n.id === notificationId);
    if (index !== -1) {
      notificationsDb[index].read = true;
    }
    return { success: true };
  },

  // Homework
  getTeacherHomework: async (schoolId, teacherId) => {
    await delay();
    const hw = homeworkDb.filter((h) => h.schoolId === schoolId && h.teacherId === teacherId);
    return { success: true, data: hw };
  },

  createHomework: async (schoolId, teacherId, data) => {
    await delay();
    const newHomework = {
      ...data,
      id: `hw-${Date.now()}`,
      schoolId,
      teacherId,
      status: 'active',
      createdAt: new Date().toISOString(),
    };
    homeworkDb.unshift(newHomework);
    return { success: true, data: newHomework };
  },

  deleteHomework: async (schoolId, homeworkId) => {
    await delay();
    homeworkDb = homeworkDb.filter(
      (h) => !(h.schoolId === schoolId && h.id === homeworkId)
    );
    return { success: true };
  },

  // Classwork
  getTeacherClasswork: async (schoolId, teacherId, date) => {
    await delay();
    let cw = classworkDb.filter((c) => c.schoolId === schoolId && c.teacherId === teacherId);
    if (date) {
      cw = cw.filter(c => c.date === date);
    }
    return { success: true, data: cw };
  },

  saveClasswork: async (schoolId, teacherId, data) => {
    await delay();
    const newCw = {
      ...data,
      id: `cw-${Date.now()}`,
      schoolId,
      teacherId,
    };
    classworkDb.unshift(newCw);
    return { success: true, data: newCw };
  },

};

export default teacherService;
